import RegExpEscape from '@packages/polyfill/regex';
import { Map } from 'immutable';
import { capitalize, each, toLower, toUpper, trim, upperFirst } from 'lodash';
import { getState } from '../redux/state';

type TransBuildProps = { [key: string]: any };

interface TransBuildOptions {
    prefix: string | any;
    props: TransBuildProps;
    before: string;
    after: string;

    lowercase: boolean;
    uppercase: boolean;
    capitalize: boolean;

    transform: (value: string) => any;
}

const REGEX_KEY = `:(.*?)[\\s${RegExpEscape('.,!?/\\[]{}()@#$%^&*":\'>=<')}]`;
const ALL_KEYS = new RegExp(REGEX_KEY, 'g');
const KEY = new RegExp(REGEX_KEY);

/**
 * Transform I18n string
 * @param transform
 * @param value
 * @returns {*}
 * @private
 */
function _t_transform(transform, value) {
    if (typeof transform === 'string') {
        switch (transform) {
            case 'lowercase':
                return toLower(value);
            case 'uppercase':
                return toUpper(value);
            case 'capitalize':
                return capitalize(value);
            default:
                return value;
        }
    }

    if (typeof transform === 'function') {
        return transform(value);
    }

    return value;
}

/**
 * I18N helper, that will build final text based on given text and props
 * @param key
 * @param props
 * @returns {*}
 * @private
 */
function _t_build(key: string, props: TransBuildProps): string {
    // TODO: insert choice support

    key += ' ';
    const matches = (key.match(ALL_KEYS) || []).map((_key) => (_key.match(KEY) || [])[1]);

    each(props, (value: any, _key: string): void => {
        if (matches.includes(_key)) {
            value = value || '';
            key = key.replace(new RegExp(RegExpEscape(':' + _key), 'g'), value);
            key = key.replace(new RegExp(RegExpEscape(':' + toUpper(_key)), 'g'), toUpper(value));
            key = key.replace(new RegExp(RegExpEscape(':' + upperFirst(_key)), 'g'), upperFirst(value));
        }
    });

    return trim(key);
}

let lastState;

/**
 * Translate helper
 * @param key
 * @param options
 * @returns {*}
 */
export function t(key: string, options: Partial<TransBuildOptions> = {}): string {
    try {
        lastState = getState();
    } catch (e) {
        lastState = {
            Internalization: Map(),
        };
    }

    const { prefix, props, before, after, transform, uppercase, lowercase, capitalize } = options;
    const { Internalization } = lastState;

    let prefix$ = prefix || Internalization.getIn(['configuration', 'prefix']) || process.env.APP_LANG_PREFIX;

    const props$ = props || {};

    prefix$ = prefix$ ? `${prefix$}.` : '';

    if (Internalization.hasIn(['configuration', 'file', `${prefix$}${key}`])) {
        key = Internalization.getIn(['configuration', 'file', `${prefix$}${key}`]);
    }

    let transformer: any = transform;

    if (!transformer) {
        if (capitalize) {
            transformer = 'capitalize';
        } else if (lowercase) {
            transformer = 'lowercase';
        } else if (uppercase) {
            transformer = 'uppercase';
        }
    }

    return `${before || ''}${_t_transform(transformer, _t_build(key, props$))}${after || ''}`;
}
